import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check, MapPin, MessageSquare, Phone, Plane, Plus } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BookingBar from "@/components/BookingBar";
import ManifestoSection from "@/components/ManifestoSection";
import FleetShowcase from "@/components/FleetShowcase";
import MapMock from "@/components/MapMock";
import StatusLine from "@/components/StatusLine";
import { IMAGES, SERVICES_INDEX } from "@/lib/data";
import { lp, HOME_COPY, p } from "@/lib/seoContent";
import { useSeo, useHreflang, JsonLd } from "@/components/seo/Seo";
import { useLang } from "@/lib/i18n";

const ease = [0.22, 1, 0.36, 1];

const FadeIn = ({ children, delay = 0, y = 24, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease }}
    className={className}
  >
    {children}
  </motion.div>
);

const TIMELINE_STATES = ["done", "done", "active", "next", "next", "next"];
const TIMELINE_TIMES = ["08:02", "08:04", "08:21", "—", "—", "—"];
const TIMELINE_KEYS = ["booked", "driver_assigned", "driver_on_the_way", "driver_arrived", "on_board", "completed"];

const TODAY_RIDES = [
  ["08:30", "Sophie Martin", "Genève → GVA", "bg-miles-electric"],
  ["10:15", "Marc Dupont", "Lausanne → ZRH", "bg-white/50"],
  ["14:00", "Client invité", "GVA → Hôtel Beau-Rivage", "bg-miles-sky"],
];

// Nom de marque communiqué à Google : « MILES » (Site Name), jamais « gomiles.ch »
const WEBSITE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "MILES",
  alternateName: ["Miles", "GoMiles", "gomiles.ch"],
  url: "https://gomiles.ch/",
};

export default function Home() {
  const { t, lang } = useLang();
  const home = HOME_COPY[lang] || HOME_COPY.fr;
  useSeo({ lang, title: home.title, description: home.meta, path: p(lang), type: "home", id: "" });
  useHreflang({ fr: "/fr", en: "/en", de: "/de" });
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 900], [0, 140]);

  return (
    <div className="min-h-screen bg-white text-ink" data-testid="home-page">
      <JsonLd data={WEBSITE_JSONLD} id="website" />
      <Navbar />

      <section className="relative z-30" data-testid="hero-section">
        <div className="grain relative min-h-[100svh] overflow-hidden">
          <picture>
            <source media="(max-width: 767px)" srcSet="/hero-miles-mobile.webp" />
            <motion.img
              src="/hero-miles.webp"
              alt={t("hero.img_alt")}
              initial={{ scale: 1.08 }}
              animate={{ scale: 1 }}
              transition={{ duration: 2.4, ease }}
              style={{ y: heroY }}
              fetchPriority="high"
              className="img-clean absolute inset-0 h-full w-full object-cover object-center"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-r from-miles-deep/80 via-miles-deep/40 to-miles-deep/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-miles-deep/70 via-transparent to-miles-deep/20" />

          <svg className="pointer-events-none absolute inset-x-0 top-24 hidden h-40 w-full opacity-60 lg:block" viewBox="0 0 1440 160" preserveAspectRatio="none" fill="none">
            <motion.path
              d="M -20 130 C 300 40 620 150 900 70 C 1120 10 1300 60 1460 30"
              stroke="#56B8FF" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="6 8"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 2.2, delay: 0.8, ease: "easeInOut" }}
            />
            <motion.circle cx="900" cy="70" r="5" fill="#56B8FF"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }} />
          </svg>

          <div className="relative mx-auto flex min-h-[100svh] max-w-7xl flex-col justify-end px-5 pb-10 pt-32 sm:px-8 lg:pb-12">
            <div className="mb-10 max-w-3xl lg:mb-14">
              <h1 className="text-[9.2vw] font-bold leading-[1.04] tracking-tight text-white sm:text-7xl sm:leading-[1.02] lg:text-[86px]">
                <motion.span
                  initial={{ opacity: 0, y: 32 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.25, ease }}
                  className="block"
                >
                  {t("hero.t1")}
                </motion.span>
                <motion.span
                  initial={{ opacity: 0, y: 32 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4, ease }}
                  className="block text-miles-electric"
                >
                  {t("hero.t2")}
                </motion.span>
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.7, ease }}
                className="mt-6 max-w-lg text-base leading-relaxed text-white/85 sm:text-lg"
              >
                <span className="italic text-lg text-white/90 sm:hidden">{t("hero.sub_short")}</span>
                <span className="hidden sm:inline">{t("hero.sub")}</span>
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 36 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.55, ease }}
            >
              <BookingBar />
            </motion.div>
          </div>
        </div>
      </section>

      <ManifestoSection />

      <FleetShowcase />

      <section className="py-24 lg:py-32" data-testid="home-services-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn>
            <div className="grid gap-6 lg:grid-cols-[1fr_auto] lg:items-end">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-miles">{t("homeSvc.eyebrow")}</p>
                <h2 className="mt-5 text-4xl font-semibold leading-[1.02] tracking-[-0.05em] text-ink sm:text-5xl">
                  {t("homeSvc.h2a")}{" "}
                  <span className="font-light text-slate/55">{t("homeSvc.h2b")}</span>
                </h2>
              </div>
              <Link to={lp(lang, "/services")} data-testid="home-services-cta"
                className="group inline-flex items-center gap-2 justify-self-start rounded-full bg-ink px-6 py-3 text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-miles lg:justify-self-end">
                {t("homeSvc.cta")}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.5} />
              </Link>
            </div>
          </FadeIn>
          <div className="mt-12 border-t border-ink/10">
            {SERVICES_INDEX.map((service, i) => {
              const item = t(`sv.items.${service.id}`);
              return (
                <FadeIn key={service.id} delay={i * 0.04} y={14}>
                  <Link to={service.href} data-testid={`home-service-row-${service.id}`}
                    className="group grid gap-1.5 border-b border-ink/10 py-6 transition-colors sm:grid-cols-[56px_1fr_auto] sm:items-center sm:gap-8">
                    <span className="font-mono text-xs font-semibold tracking-[0.2em] text-miles">{String(i + 1).padStart(2, "0")}</span>
                    <span>
                      <span className="block text-xl font-semibold tracking-tight text-ink transition-transform duration-300 group-hover:translate-x-1.5 sm:text-2xl">{item.name}</span>
                      <span className="mt-1 block max-w-2xl text-sm leading-relaxed text-slate">{item.desc}</span>
                    </span>
                    <ArrowUpRight className="hidden h-5 w-5 text-slate transition-[color,transform] duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-miles sm:block" strokeWidth={1.5} />
                  </Link>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      <section className="grain relative overflow-hidden bg-miles-deep py-24 lg:py-36" data-testid="tracking-section">
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid items-end gap-8 lg:grid-cols-[1fr_auto]">
            <FadeIn>
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-miles-electric">{t("homeTrack.eyebrow")}</p>
              <h2 className="mt-5 max-w-2xl text-3xl font-semibold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
                {t("homeTrack.h2")}
              </h2>
            </FadeIn>
            <FadeIn delay={0.1}>
              <Link to="/suivi" data-testid="live-tracking-demo-cta"
                className="group inline-flex items-center gap-2 rounded-full border border-miles-sky/30 px-6 py-3 text-sm font-semibold text-white transition-colors duration-300 hover:border-miles-sky">
                {t("homeTrack.cta")}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.5} />
              </Link>
            </FadeIn>
          </div>

          <FadeIn delay={0.15}>
            <div className="relative mt-14 lg:mt-16">
              <div className="grid overflow-hidden rounded-3xl bg-white shadow-[0_48px_120px_-24px_rgba(0,0,0,0.55)] lg:grid-cols-[1.35fr_1fr]" data-testid="tracking-preview-card">
                <MapMock progress={0.55} fromLabel="Genève — Centre" toLabel="Aéroport GVA" height="h-72 sm:h-full sm:min-h-[420px]" />
                <div className="flex flex-col justify-between gap-8 p-7 sm:p-9">
                  <div>
                    <div className="flex items-center justify-between gap-3">
                      <span className="inline-flex items-center gap-2 rounded-full bg-miles px-3.5 py-1.5 text-[11px] font-bold text-white">
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="ping-soft absolute inline-flex h-full w-full rounded-full bg-white" />
                          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white" />
                        </span>
                        {t("homeTrack.chip")}
                      </span>
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate">Genève → GVA</span>
                    </div>
                    <p className="mt-6 text-2xl font-semibold tracking-tight text-ink sm:text-3xl">
                      {t("homeTrack.arrive")}
                    </p>
                    <p className="mt-1.5 text-sm text-slate">{t("homeTrack.car")} · <span className="font-mono font-semibold text-ink">GE 512 408</span></p>
                    <div className="mt-5 flex gap-2.5">
                      <span className="inline-flex items-center gap-2 rounded-full bg-miles px-5 py-2.5 text-xs font-semibold text-white">
                        <Phone className="h-3.5 w-3.5" strokeWidth={1.5} /> {t("homeTrack.call")}
                      </span>
                      <span className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-5 py-2.5 text-xs font-semibold text-ink">
                        <MessageSquare className="h-3.5 w-3.5" strokeWidth={1.5} /> {t("homeTrack.message")}
                      </span>
                    </div>
                  </div>
                  <div className="border-t border-ink/10 pt-6" data-testid="tracking-timeline-demo">
                    <div className="flex items-center justify-between">
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate">{t("homeTrack.eta")}</p>
                      <p className="font-mono text-2xl font-semibold tracking-tight text-ink">08:45</p>
                    </div>
                    <div className="mt-5 space-y-0">
                      {TIMELINE_KEYS.map((key, i) => {
                        const state = TIMELINE_STATES[i];
                        return (
                          <div key={key} className="relative flex items-center gap-3 pb-3.5 last:pb-0">
                            {i < TIMELINE_KEYS.length - 1 && (
                              <span className={`absolute left-[3.5px] top-4 h-full w-px ${state === "done" ? "bg-miles/40" : "bg-ink/10"}`} />
                            )}
                            <span className={`relative h-2 w-2 shrink-0 rounded-full ${
                              state === "done" ? "bg-miles" : state === "active" ? "bg-miles ring-4 ring-miles/15" : "border border-ink/20 bg-white"
                            }`} />
                            <p className={`flex-1 text-xs font-semibold ${state === "next" ? "text-ink/35" : "text-ink"}`}>{t(`statuses.${key}`)}</p>
                            <p className="font-mono text-[10px] text-slate">{TIMELINE_TIMES[i]}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 16, rotate: -1.5 }}
                whileInView={{ opacity: 1, y: 0, rotate: -1.5 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="absolute -top-6 right-6 hidden rounded-2xl bg-white p-4 shadow-[0_16px_48px_rgba(0,0,0,0.35)] lg:block"
                data-testid="tracking-notification-chip"
              >
                <p className="text-xs font-bold text-ink">{t("homeTrack.notif_t")}</p>
                <p className="mt-0.5 text-xs text-slate">{t("homeTrack.notif_b")}</p>
              </motion.div>
            </div>
          </FadeIn>

          <FadeIn delay={0.2}>
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-miles-sky/50">{t("homeTrack.caption")}</p>
              <StatusLine current="driver_on_the_way" dark testId="status-line-home" />
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="py-24 lg:py-36" data-testid="expectations-section">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[1fr_1.2fr] lg:gap-24">
          <FadeIn>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-miles">{t("expect.eyebrow")}</p>
            <h2 className="mt-5 text-3xl font-semibold leading-[1.08] tracking-tight sm:text-5xl">
              {t("expect.h2a")}
              <br />
              <span className="font-light text-slate/60">{t("expect.h2b")}</span>
            </h2>
            <p className="mt-6 max-w-sm text-base leading-relaxed text-slate">{t("expect.p")}</p>
          </FadeIn>
          <div>
            {t("expect.items").map(([label, detail], i) => (
              <FadeIn key={label} delay={i * 0.06}>
                <div className="relative flex gap-5 pb-8 last:pb-0" data-testid={`expectation-${i}`}>
                  {i < t("expect.items").length - 1 && <span className="absolute left-[5px] top-4 h-full w-px bg-ink/10" />}
                  <span className="relative mt-1.5 h-[11px] w-[11px] shrink-0 rounded-full border-2 border-miles bg-white" />
                  <div>
                    <p className="text-base font-semibold tracking-tight text-ink sm:text-lg">{label}</p>
                    <p className="mt-1 text-sm text-slate">{detail}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden" data-testid="interlude-section">
        <motion.div
          initial={{ scale: 1.08 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 2, ease }}
          className="relative h-[68vh] min-h-[480px]"
        >
          <img src={IMAGES.sedanDark} alt="Berline MILES sur route de nuit" loading="lazy"
            className="img-clean absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-miles-deep/75 via-miles-deep/20 to-miles-deep/30" />
          <div className="absolute inset-0 flex items-end">
            <div className="mx-auto w-full max-w-7xl px-5 pb-14 sm:px-8 lg:pb-20">
              <FadeIn>
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-miles-sky">{t("interlude.eyebrow")}</p>
                <h2 className="mt-4 max-w-3xl text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
                  {t("interlude.h2a")}
                  <br />
                  <span className="font-light text-white/70">{t("interlude.h2b")}</span>
                </h2>
                <Link to="/reservation" data-testid="interlude-cta"
                  className="group mt-7 inline-flex items-center gap-2 text-sm font-semibold text-white">
                  {t("interlude.cta")}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
                </Link>
              </FadeIn>
            </div>
          </div>
        </motion.div>
      </section>

      <section className="relative overflow-hidden py-24 lg:py-36" data-testid="airport-section">
        <img src={IMAGES.planeWing} alt="Aile d'avion au-dessus des nuages" loading="lazy"
          className="img-clean absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-miles-deep/90 via-miles-deep/70 to-miles-deep/40" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20">
          <FadeIn>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-miles-electric">{t("homeAirport.eyebrow")}</p>
            <h2 className="mt-5 text-3xl font-semibold leading-[1.08] tracking-tight text-white sm:text-5xl">
              {t("homeAirport.h2a")}
              <br />
              <span className="font-light text-white/60">{t("homeAirport.h2b")}</span>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-miles-sky/80">{t("homeAirport.p")}</p>
            <Link to={lp(lang, "/aeroports")} data-testid="airport-card-cta"
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-miles-deep transition-all duration-300 hover:-translate-y-0.5">
              {t("homeAirport.cta")}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.5} />
            </Link>
          </FadeIn>
          <FadeIn delay={0.15}>
            <div className="glass-dark rounded-3xl p-7 shadow-[0_40px_100px_-24px_rgba(0,0,0,0.5)] sm:p-9" data-testid="flight-tracker-ui">
              <div className="flex items-center justify-between">
                <p className="flex items-center gap-2 font-mono text-sm font-bold text-white">
                  <Plane className="h-4 w-4 text-miles-electric" strokeWidth={1.5} /> LX 2807
                </p>
                <span className="rounded-full bg-amber-400/15 px-3 py-1 text-[11px] font-bold text-amber-300">{t("homeAirport.delay")}</span>
              </div>
              <p className="mt-1.5 font-mono text-[11px] text-miles-sky/60">{t("homeAirport.route")}</p>
              <div className="mt-6 space-y-3 border-t border-white/10 pt-6 font-mono text-sm">
                {[[t("homeAirport.l1"), "18:42", "text-white/50"], [t("homeAirport.l2"), "19:04", "text-white"], [t("homeAirport.l3"), "19:24", "text-miles-electric"]].map(([k, v, tone]) => (
                  <div key={k} className="flex items-baseline justify-between">
                    <span className="text-xs uppercase tracking-[0.15em] text-miles-sky/60">{k}</span>
                    <span className={`font-semibold ${tone}`}>{v}</span>
                  </div>
                ))}
              </div>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.6 }}
                className="mt-6 flex items-center gap-2.5 rounded-2xl bg-miles/20 px-4 py-3.5 text-sm font-semibold text-white"
              >
                <Check className="h-4 w-4 shrink-0 text-miles-electric" strokeWidth={2} />
                {t("homeAirport.adjusted")}
              </motion.p>
              <div className="mt-4 flex items-center justify-between rounded-2xl border border-white/10 px-4 py-3.5" data-testid="meeting-point-card">
                <p className="flex items-center gap-2.5 text-sm text-white">
                  <MapPin className="h-4 w-4 shrink-0 text-miles-electric" strokeWidth={1.5} />
                  {t("homeAirport.meeting")}
                </p>
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-miles-sky/60">{t("homeAirport.meeting_l")}</span>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="grain relative overflow-hidden bg-miles-deep py-20 lg:py-32" data-testid="business-section">
        <svg className="pointer-events-none absolute -right-40 top-0 h-full w-[800px] opacity-25" viewBox="0 0 800 600" fill="none">
          <path d="M 0 500 C 200 380 350 480 520 320 C 640 210 720 260 800 180" stroke="#56B8FF" strokeWidth="1.5" strokeDasharray="6 8" className="route-line" />
        </svg>
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid items-center gap-14 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
            <FadeIn>
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-miles-electric">{t("homeBiz.eyebrow")}</p>
              <h2 className="mt-4 text-3xl font-semibold leading-[1.08] tracking-tight text-white sm:text-4xl lg:text-5xl">
                {t("homeBiz.h2")}
              </h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-miles-sky/80">{t("homeBiz.p")}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to={lp(lang, "/contact")} data-testid="business-demo-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-miles-deep transition-all duration-300 hover:-translate-y-0.5">
                  {t("homeBiz.demo")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                </Link>
                <Link to="/business/app" data-testid="business-explore-cta"
                  className="inline-flex items-center gap-2 rounded-full border border-miles-sky/30 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:border-miles-sky">
                  {t("homeBiz.explore")}
                </Link>
              </div>
            </FadeIn>

            <FadeIn delay={0.15}>
              <div className="relative">
                <div className="overflow-hidden rounded-3xl border border-white/10 bg-white/[0.05] shadow-[0_48px_120px_-24px_rgba(0,0,0,0.6)] backdrop-blur-sm" data-testid="business-dashboard-preview">
                  <div className="grid sm:grid-cols-[150px_1fr]">
                    <div className="hidden border-r border-white/10 p-5 sm:block">
                      {t("homeBiz.side").map((n, i) => (
                        <p key={n} className={`rounded-lg px-3 py-2 font-mono text-[11px] uppercase tracking-[0.15em] ${
                          i === 0 ? "bg-miles/20 text-white" : "text-miles-sky/40"
                        }`}>
                          {n}
                        </p>
                      ))}
                    </div>
                    <div className="p-5 sm:p-7">
                      <div className="flex items-center justify-between gap-3">
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-miles-sky/60">{t("homeBiz.today")}</p>
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-miles px-4 py-2 text-[11px] font-bold text-white">
                          <Plus className="h-3 w-3" strokeWidth={2.5} /> {t("homeBiz.new")}
                        </span>
                      </div>
                      <div className="mt-5 space-y-2" data-testid="business-today-rides">
                        {TODAY_RIDES.map(([time, who, route, dot], i) => (
                          <div key={time} className="flex items-center gap-3 rounded-xl bg-white/5 px-4 py-3">
                            <span className="font-mono text-xs font-semibold text-miles-sky/80">{time}</span>
                            <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dot}`} />
                            <p className="truncate text-sm font-semibold text-white">{who}</p>
                            <p className="hidden flex-1 truncate text-xs text-miles-sky/60 sm:block">{route}</p>
                            <p className="ml-auto font-mono text-[10px] uppercase tracking-wider text-miles-sky/80">{t("homeBiz.ride_status")[i]}</p>
                          </div>
                        ))}
                      </div>
                      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-5">
                        <div className="flex gap-1.5 rounded-full bg-white/5 p-1" data-testid="booking-for-selector">
                          {t("homeBiz.for").map((o) => (
                            <span key={o} className={`rounded-full px-3.5 py-1.5 text-[11px] font-semibold ${
                              o === t("homeBiz.for")[2] ? "bg-miles text-white" : "text-miles-sky/60"
                            }`}>
                              {o}
                            </span>
                          ))}
                        </div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-miles-sky/50">{t("homeBiz.noacct")}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 16, rotate: 1.5 }}
                  whileInView={{ opacity: 1, y: 0, rotate: 1.5 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.55 }}
                  className="absolute -bottom-6 -left-4 rounded-2xl bg-white p-4 shadow-[0_20px_60px_rgba(0,0,0,0.4)] sm:-left-8"
                  data-testid="business-invoice-chip"
                >
                  <p className="text-xs font-bold text-ink">{t("homeBiz.inv_t")}</p>
                  <p className="mt-0.5 text-xs text-slate">{t("homeBiz.inv_b")}</p>
                </motion.div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="py-28 lg:py-40" data-testid="cities-cta-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-miles">{t("citiesCta.eyebrow")}</p>
            <h2 className="mt-6 max-w-4xl text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              {t("citiesCta.h2a")}
              <br />
              <span className="font-light text-slate/50">{t("citiesCta.h2b")}</span>
            </h2>
          </FadeIn>
          <FadeIn delay={0.15}>
            <div className="mt-12 flex flex-wrap items-center gap-4">
              <Link to="/reservation" data-testid="cities-book-cta"
                className="inline-flex items-center gap-2 rounded-full bg-miles px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-miles-dark">
                {t("citiesCta.book")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </Link>
              <Link to={lp(lang, "/villes")} data-testid="cities-explore-link"
                className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-8 py-4 text-sm font-semibold text-ink transition-colors hover:border-ink">
                {t("citiesCta.cities")}
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      <Footer />
    </div>
  );
}
